<template>
  <div class="my-account p-6">
    <div class="max-w-4xl mx-auto">
      <h1 class="text-2xl font-bold mb-6">Minha Conta</h1>

      <div class="bg-white rounded-lg shadow">
        <!-- Abas -->
        <div class="border-b px-6">
          <nav class="flex -mb-px">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="currentTab = tab.id"
              :class="[
                'py-4 px-6 text-sm font-medium border-b-2 -mb-px transition-colors duration-200',
                currentTab === tab.id
                  ? 'border-primary-dark text-primary-dark'
                  : 'border-transparent text-gray-500 hover:text-primary-dark/70 hover:border-primary-dark/30'
              ]"
            >
              <i :class="['mr-2', tab.icon]"></i>
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <!-- Conteúdo -->
        <div class="p-6">
          <!-- Informações Pessoais -->
          <div v-if="currentTab === 'personal'" class="space-y-6">
            <form @submit.prevent="handlePersonalInfoSubmit" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Nome Completo</label>
                <input
                  v-model="personalForm.name"
                  type="text"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                  required
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700">Email</label>
                <input
                  v-model="personalForm.email"
                  type="email"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark bg-gray-50"
                  disabled
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700">Telefone</label>
                <input
                  v-model="personalForm.phone"
                  type="tel"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700">Cargo</label>
                <input
                  v-model="personalForm.position"
                  type="text"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                />
              </div>

              <div class="flex justify-end">
                <button
                  type="submit"
                  class="bg-primary-dark text-white px-4 py-2 rounded hover:bg-primary-dark/90 transition-colors disabled:opacity-50"
                  :disabled="loadingPersonal"
                >
                  <i v-if="loadingPersonal" class="pi pi-spin pi-spinner mr-2"></i>
                  Salvar Alterações
                </button>
              </div>
            </form>
          </div>

          <!-- Segurança -->
          <div v-if="currentTab === 'security'" class="space-y-6">
            <!-- Two-Factor Authentication -->
            <div class="bg-white shadow rounded-lg p-6 mb-6">
              <h3 class="text-lg font-medium text-gray-900 mb-4">Autenticação em Dois Fatores</h3>
              <TwoFactorSetup />
            </div>

            <!-- Password Change Form -->
            <form @submit.prevent="handlePasswordSubmit" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Senha Atual</label>
                <input
                  v-model="passwordForm.currentPassword"
                  type="password"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Nova Senha</label>
                <input
                  v-model="passwordForm.newPassword"
                  type="password"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Confirmar Nova Senha</label>
                <input
                  v-model="passwordForm.confirmPassword"
                  type="password"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                  required
                />
              </div>

              <div class="flex justify-end">
                <button
                  type="submit"
                  class="bg-primary-dark text-white px-4 py-2 rounded hover:bg-primary-dark/90 transition-colors disabled:opacity-50"
                  :disabled="loadingPassword"
                >
                  <i v-if="loadingPassword" class="pi pi-spin pi-spinner mr-2"></i>
                  Alterar Senha
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'primevue/usetoast'
import TwoFactorSetup from '@/components/TwoFactorSetup.vue'

const toast = useToast()
const authStore = useAuthStore()

const currentTab = ref('personal')
const loadingPersonal = ref(false)
const loadingPassword = ref(false)

const tabs = [
  { id: 'personal', name: 'Informações Pessoais', icon: 'pi pi-user' },
  { id: 'security', name: 'Segurança', icon: 'pi pi-shield' }
]

const personalForm = ref({
  name: '',
  email: '',
  phone: '',
  position: ''
})

const passwordForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

onMounted(async () => {
  // Carregar dados do usuário
  if (authStore.userData) {
    personalForm.value = {
      name: authStore.userData.name || '',
      email: authStore.userData.email || '',
      phone: authStore.userData.phone || '',
      position: authStore.userData.position || ''
    }
  }
})

const handlePersonalInfoSubmit = async () => {
  try {
    loadingPersonal.value = true
    await authStore.updateUserProfile(personalForm.value)
    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Informações pessoais atualizadas com sucesso!',
      life: 3000
    })
  } catch (error) {
    console.error('Erro ao atualizar informações:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Falha ao atualizar informações pessoais.',
      life: 3000
    })
  } finally {
    loadingPersonal.value = false
  }
}

const handlePasswordSubmit = async () => {
  try {
    if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
      toast.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'As senhas não coincidem',
        life: 3000
      })
      return
    }

    loadingPassword.value = true
    await authStore.updatePassword({
      currentPassword: passwordForm.value.currentPassword,
      newPassword: passwordForm.value.newPassword
    })

    // Limpar o formulário
    passwordForm.value = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }

    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Senha atualizada com sucesso!',
      life: 3000
    })
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Não foi possível atualizar a senha.',
      life: 3000
    })
  } finally {
    loadingPassword.value = false
  }
}
</script>
